<template>
  <q-form @submit="this.onSubmit" class="ms-form">
    <div class="ms-form-grid">
      <CustomSelect
        :options="this.companies"
        label="Компания (заказчик)"
        :required="true"
        optionValueKey="company_id"
        optionLabelKey="company_name"
        :value="this.formData.company_id || ''"
        @change="(value) => onChange('company_id', value)"
      />
      <CustomSelect
        :options="this.agreements"
        label="Договор"
        :required="true"
        optionValueKey="contractor_agreement_list_id"
        optionLabelKey="contractor_agreement_list_name"
        :value="this.formData.contractor_agreement_list_id || ''"
        @change="(value) => onChange('contractor_agreement_list_id', value)"
      />
      <CustomDate
        label="Дата создания"
        :fullWidth="true"
        :required="true"
        :value="this.formData.avr_date || ''"
        @change_date="(value) => onChange('avr_date', value)"
      />
      <div>
        <CustomInput
          label="Сумма АВР"
          type="number"
          :allowNegative="false"
          :integer="true"
          :required="!this.formData.is_for_full_agreement_sum"
          :disable="!!this.formData.is_for_full_agreement_sum"
          :value="this.formData.avr_sum"
          @change_input="(value) => onChange('avr_sum', value)"
        />
        <div
          v-if="this.formData.is_for_full_agreement_sum && this.fullAgreementSumPreview != null"
          class="form__sum-hint"
        >
          Будет создан АВР на {{ this.fullAgreementSumPreview }} ₸
          <span class="form__sum-hint-note">(остаток по договору)</span>
        </div>
      </div>
      <div class="ms-form-wide">
        <CustomCheckbox
          :value="!!this.formData.is_for_full_agreement_sum"
          label="На всю сумму договора"
          @change="(value) => onChange('is_for_full_agreement_sum', value)"
        />
      </div>
      <div v-if="this.showSendWithoutSpec" class="ms-form-wide form__north-card">
        <div class="form__north-card-title">Временный вариант для {{ this.withoutSpecCompanyLabel }}</div>
        <div class="form__north-banner" role="status">
          <q-icon name="warning_amber" size="18px" class="form__north-banner-icon" />
          <div class="form__north-banner-text">
            АВР создаётся на компанию {{ this.withoutSpecCompanyLabel }}, при этом спецификация была оформлена на ТОО «Smart Remont».
          </div>
        </div>
        <CustomCheckbox
          :value="!!this.formData.send_without_spec"
          :label="`Отправить в ${this.withoutSpecCompanyLabel} без спецификации`"
          @change="(value) => onChange('send_without_spec', value)"
        />
      </div>
    </div>
    <CustomModalFooter :btn-loading="this.loading"/>
  </q-form>
</template>

<script>
import CustomModalFooter from "@/components/modal/modals/modal-footer";
import CustomInput from "@/components/form/CustomInput";
import CustomSelect from "@/components/form/CustomSelect";
import CustomCheckbox from "@/components/form/CustomCheckbox";
import CustomDate from "@/components/form/CustomDate";
import {
  listContractorAgreementForAvrModal,
  listCompaniesForAvrSelect,
} from "@/views/ClientRequests/contragentAVR/services";
import { appActions } from "@/services/store/modules/app_module/app_actions";
import { getLocaleDate } from "@/services";

/** company_tab.company_id → короткое имя для WITHOUT_SPEC */
const WITHOUT_SPEC_COMPANY_LABELS = {
  9: "South",
  11: "North",
};

/** group_type_code из list_contractor_agreement_for_avr_modal */
const DESIGN_OR_SUPPORT_GROUP_CODES = new Set([
  "DESIGN_AGREEMENT",
  "SUPPORT_AGREEMENT",
]);

export default {
  name: "add-avr",
  components: {
    CustomInput,
    CustomModalFooter,
    CustomSelect,
    CustomCheckbox,
    CustomDate,
  },
  props: ["data"],
  data() {
    return {
      loading: false,
      isFetching: false,
      agreements: [],
      companies: [],
      formData: {
        avr_sum: "",
        is_for_full_agreement_sum: false,
        send_without_spec: false,
        contractor_agreement_list_id: "",
        company_id: this.data?.company_id != null && this.data?.company_id !== ""
          ? Number(this.data.company_id)
          : "",
        avr_date: getLocaleDate(new Date()),
      },
    };
  },

  computed: {
    selectedAgreement() {
      const id = this.formData.contractor_agreement_list_id;
      if (id === "" || id == null) return null;
      return (
        this.agreements.find(
          (a) => Number(a.contractor_agreement_list_id) === Number(id),
        ) || null
      );
    },
    withoutSpecCompanyLabel() {
      return WITHOUT_SPEC_COMPANY_LABELS[Number(this.formData.company_id)] || null;
    },
    isDesignOrSupportSpec() {
      const group = this.selectedAgreement?.group_type_code;
      return DESIGN_OR_SUPPORT_GROUP_CODES.has(String(group || ""));
    },
    showSendWithoutSpec() {
      return !!this.withoutSpecCompanyLabel && this.isDesignOrSupportSpec;
    },
    /** Превью суммы при «на всю сумму»: agreement_sum из договора (бэкенд вычитает уже подписанные АВР). */
    fullAgreementSumPreview() {
      const sum = this.selectedAgreement?.agreement_sum;
      if (sum == null || sum === "") return null;
      const n = Number(sum);
      if (!Number.isFinite(n)) return null;
      return n.toLocaleString("ru-RU", {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
      });
    },
  },

  methods: {
    onChange(key, value) {
      if (key === "contractor_agreement_list_id") {
        this.formData.contractor_agreement_list_id = value;
        this.formData.send_without_spec = false;
        return;
      }

      if (key === "company_id") {
        this.formData.company_id =
          value != null && value !== "" ? Number(value) : "";
        this.formData.send_without_spec = false;
        return;
      }

      this.formData[key] = value;
    },

    async onSubmit() {
      this.loading = true;
      try {
        await this.data?.onSubmit({
          contractor_agreement_list_id: this.formData.contractor_agreement_list_id,
          client_request_id: this.data.client_request_id,
          avr_sum: this.formData.is_for_full_agreement_sum
            ? null
            : this.formData.avr_sum === ""
              ? null
              : this.formData.avr_sum,
          company_id: this.formData.company_id
            ? Number(this.formData.company_id)
            : null,
          is_for_full_agreement_sum: !!this.formData.is_for_full_agreement_sum,
          avr_date: this.formData.avr_date,
          send_mode: this.formData.send_without_spec
            ? "WITHOUT_SPEC"
            : "STANDARD",
        });
      } finally {
        this.loading = false;
      }
    },

    async getAgreements() {
      this.isFetching = true;
      const res = await listContractorAgreementForAvrModal(this.data.client_request_id);
      this.isFetching = false;
      if (!res) {
        this.$store.commit(appActions.closeModal());
        return;
      }
      // Убираем опцию «Без спецификации» (null id / is_without_specification)
      this.agreements = (res || []).filter(
        (item) =>
          item.contractor_agreement_list_id != null &&
          Number(item.is_without_specification) !== 1,
      );
    },

    async getCompanies() {
      const rows = await listCompaniesForAvrSelect();
      this.companies = (rows || []).map((c) => ({
        ...c,
        company_name_official: c.company_name_official || c.company_name,
      }));
      if (!this.formData.company_id && this.data?.company_id != null) {
        this.formData.company_id = Number(this.data.company_id);
      }
    },
  },

  mounted() {
    this.getCompanies();
    this.getAgreements();
  },
};
</script>

<style scoped lang="scss">
// Сетка — общая (ms-form-* в theme.scss)
.form {
  &__sum-hint {
    margin-top: 8px;
    font-size: 13px;
    font-weight: 500;
    color: var(--ms-primary);
  }

  &__sum-hint-note {
    font-weight: 400;
    color: var(--ms-text-secondary);
  }

  &__north-card {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px;
    border-radius: 10px;
    border: 1px solid #ffe082;
    background: #fffdf5;
  }

  &__north-card-title {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: #8a6d1a;
  }

  &__north-banner {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 8px;
    background: #fff8e1;
    border: 1px solid #ffe082;
    color: #7a5a00;
    font-size: 12px;
    line-height: 1.4;
  }

  &__north-banner-icon {
    flex-shrink: 0;
    margin-top: 1px;
    color: #f9a825;
  }

  &__north-banner-text {
    flex: 1;
  }
}
</style>
